import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { preloadAuthenticatedRoutes } from '../utils/routePreloader';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_verified: boolean;
  onboarding_completed?: boolean;
  last_login_at?: string;
}

interface AuthError {
  message: string;
  code?: 'INVALID_CREDENTIALS' | 'ACCOUNT_LOCKED' | 'SECURITY_PASSWORD_RESET_REQUIRED' | 'UNKNOWN';
  details?: {
    lockout_duration?: number;
    account_locked?: boolean;
    retry_after?: number;
    password_reset_required?: boolean;
    reset_reason?: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean; // New: tracks initial hydration
  error: AuthError | null;
  login: (email: string, password: string, rememberDevice?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshAccessToken,
    clearError,
    accessToken,
    fetchUserProfile,
  } = useAuthStore();

  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitialized = React.useRef(false);

  // Hydrate auth state on mount
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (hasInitialized.current) {
      return;
    }

    const initializeAuth = async () => {
      if (import.meta.env.DEV) {
        console.log('[AuthContext] Initializing authentication...');
      }
      hasInitialized.current = true;

      // Check if we have a token in the store
      if (accessToken && isAuthenticated) {
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Found existing token, verifying...');
        }

        try {
          // Verify token is still valid by fetching user profile
          await fetchUserProfile();
          if (import.meta.env.DEV) {
            console.log('[AuthContext] Token valid, user authenticated');
          }

          // Sync onboarding store with current user (clear if different user)
          if (user?.id) {
            const { useOnboardingStore } = await import('../stores/onboardingStore');
            useOnboardingStore.getState().syncUserId(user.id);
          }

          // Preload authenticated routes after successful auth verification
          // Only preload onboarding if user hasn't completed it
          const onboardingCompleted = user?.onboarding_completed ?? true;
          preloadAuthenticatedRoutes(onboardingCompleted);
        } catch (error) {
          console.error('[AuthContext] Token validation failed:', error);
          // Token is invalid, clear auth state
          logout();
        }
      } else {
        if (import.meta.env.DEV) {
          console.log('[AuthContext] No existing token, user not authenticated');
        }
      }

      // Hydration complete
      setIsInitializing(false);
      if (import.meta.env.DEV) {
        console.log('[AuthContext] Initialization complete');
      }
    };

    initializeAuth();
  }, []); // Only run once on mount

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isInitializing,
    error,
    login,
    logout,
    refreshAccessToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
